"use client";

import type { ReactNode } from "react";
import { Button } from "./Button";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "./Dialog";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for a destructive action — the only kind this app
   *  currently asks "are you sure?" about. */
  variant?: "destructive" | "primary";
  /** Disables both buttons and swaps the confirm label while the action runs. */
  pending?: boolean;
  pendingLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra content between header and footer — a rule picker, an error line
   *  (OSO-663). Rendered in a `DialogBody` only when present, so the plain
   *  two-button confirm keeps its compact shape. */
  children?: ReactNode;
}

/**
 * The app's one "are you sure?" primitive, built on Dialog rather than
 * window.confirm/prompt — those are disallowed by the ratchet check
 * (see ratchets.json) because a native browser prompt can't be themed and
 * ignores dark mode.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Bekræft",
  cancelLabel = "Annuller",
  variant = "destructive",
  pending = false,
  pendingLabel = "Vent…",
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={pending ? undefined : onCancel}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      {children && <DialogBody className="space-y-2">{children}</DialogBody>}
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={pending}>
          {pending ? pendingLabel : confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
